// piano-frontend/src/pages/MySchedulePage.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Card,
  CardContent,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert
} from "@mui/material";
import {
  Piano,
  LibraryMusic,
  Close,
  AccessTime,
  Person,
  HourglassEmpty
} from "@mui/icons-material";
import dayjs from "dayjs";
import api from "../api/axios";
import { useDarkMode } from "../App";
import MonthCalendarNav from "../components/MonthCalendarNav";
import { useMonthParam } from "../hooks/useCalendarUrlState";

function MySchedulePage({ user }) {
  const { darkMode } = useDarkMode();
  const [year, month, setYearMonth] = useMonthParam(); // 보던 달은 URL(?month=)에 유지
  const [lessons, setLessons] = useState([]);
  const [practices, setPractices] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [dailySchedule, setDailySchedule] = useState([]);
  
  const today = dayjs();
  const isAdminOrTeacher = user?.role === 'ADMIN' || user?.role === 'TEACHER';

  useEffect(() => {
    fetchMonthSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const fetchMonthSchedule = async () => {
    // 250ms 안에 끝나는 조회는 로딩 막을 띄우지 않는다 — 월 전환마다 검정 막이 깜빡이는 것을 막는다
    const loaderTimer = setTimeout(() => setShowLoader(true), 250);
    try {
      const startDate = dayjs(`${year}-${month + 1}-01`).format("YYYY-MM-DD");
      const endDate = dayjs(`${year}-${month + 1}-01`).endOf("month").format("YYYY-MM-DD");

      // 두 요청은 서로 의존하지 않으므로 병렬로 보낸다 (로딩 구간 단축)
      const [lessonRes, practiceRes] = await Promise.all([
        // 학원 전체 예약을 받아 클라이언트에서 거르지 않고, 서버에서 내 예약만 받는다
        api.get("/lessons/my", {
          params: {
            start_date: startDate,
            end_date: endDate
          }
        }),
        api.get("/practices/my-reservations", {
          params: {
            start_date: startDate,
            end_date: endDate
          }
        })
      ]);

      setLessons(lessonRes.data);
      setPractices(practiceRes.data);
    } catch (error) {
      console.error("일정 조회 실패:", error);
    } finally {
      clearTimeout(loaderTimer);
      setShowLoader(false);
    }
  };

  // ✅ 정식/임시 레슨 분리 카운트
  const getScheduleCountByDate = (dateStr) => {
    const regularLessons = lessons.filter(l => l.date === dateStr && !l.is_provisional).length;
    const provisionalLessons = lessons.filter(l => l.date === dateStr && l.is_provisional).length;
    const practiceCount = practices.filter(p => p.date === dateStr).length;
    
    return { regularLessons, provisionalLessons, practiceCount };
  };

  const firstDay = dayjs(`${year}-${month + 1}-01`);
  const firstDow = firstDay.day();
  const lastDate = firstDay.endOf("month").date();
  const prevMonthLastDate = firstDay.subtract(1, "month").endOf("month").date();
  const calendarDays = [];

  for (let i = firstDow - 1; i >= 0; i--) {
    calendarDays.push({
      day: prevMonthLastDate - i,
      isCurrentMonth: false,
      month: month === 0 ? 11 : month - 1,
      year: month === 0 ? year - 1 : year
    });
  }

  for (let day = 1; day <= lastDate; day++) {
    calendarDays.push({
      day,
      isCurrentMonth: true,
      month,
      year
    });
  }

  const remainingDays = 42 - calendarDays.length;
  for (let day = 1; day <= remainingDays; day++) {
    calendarDays.push({
      day,
      isCurrentMonth: false,
      month: month === 11 ? 0 : month + 1,
      year: month === 11 ? year + 1 : year
    });
  }

  const handleDateClick = (dateInfo) => {
    const { day, month: m, year: y } = dateInfo;
    const dateStr = dayjs(`${y}-${m + 1}-${day}`).format("YYYY-MM-DD");
    const { regularLessons, provisionalLessons, practiceCount } = getScheduleCountByDate(dateStr);
    
    if ((regularLessons > 0 || provisionalLessons > 0 || practiceCount > 0) && dateInfo.isCurrentMonth) {
      const dayLessons = lessons.filter(l => l.date === dateStr);
      const dayPractices = practices.filter(p => p.date === dateStr);
      
      setSelectedDate(dateStr);
      setDailySchedule([
        ...dayLessons.map(l => ({ ...l, type: 'lesson' })),
        ...dayPractices.map(p => ({ ...p, type: 'practice' }))
      ].sort((a, b) => a.start_time.localeCompare(b.start_time)));
      setDetailDialog(true);
    }
  };

  const regularLessonsTotal = lessons.filter(l => !l.is_provisional).length;
  const provisionalLessonsTotal = lessons.filter(l => l.is_provisional).length;

  // 날짜 칸의 일정 표시용 색 점 (같은 유형이 2개 이상이면 개수 숫자 병기)
  const scheduleDot = (color, count, key) => (
    <Box key={key} sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
      {count > 1 && (
        <Typography component="span" sx={{ fontSize: "0.6rem", lineHeight: 1, fontWeight: 700, color: darkMode ? "#ccc" : "#555" }}>
          {count}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ pt: { xs: 1, sm: 2 }, pb: 1.5, maxWidth: 900, mx: "auto", px: { xs: 1, sm: 2 } }}>
      {/* 모바일은 헤더에 페이지 제목이 표시되므로 본문 제목 숨김 */}
      <Typography variant="h5" align="center" fontWeight="bold" sx={{ mb: 3, display: { xs: "none", sm: "block" } }}>
        내 일정
      </Typography>

      <Paper
        elevation={2}
        sx={{
          px: { xs: 1, sm: 3 },
          py: { xs: 2, sm: 3 },
          mb: 1.5,
          position: "relative",
          overflow: 'hidden',
        }}
      >
        {showLoader && (
          <Box sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: darkMode ? "rgba(30,30,30,0.55)" : "rgba(255,255,255,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10
          }}>
            <CircularProgress />
          </Box>
        )}

        {/* 지난 일정 조회용이라 연 단위 점프까지 허용 */}
        <MonthCalendarNav year={year} month={month} onChange={setYearMonth} allowYearView>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, mb: 1 }}>
            {["일", "월", "화", "수", "목", "금", "토"].map((d, i) => (
              <Box key={i} sx={{
                textAlign: "center",
                fontWeight: "bold",
                py: 1,
                color: i === 0 ? "#d32f2f" : i === 6 ? "#1976d2" : "inherit"
              }}>{d}</Box>
            ))}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
        {calendarDays.map((dateInfo, idx) => {
            const { day, isCurrentMonth, month: m, year: y } = dateInfo;
            const dateObj = dayjs(`${y}-${m + 1}-${day}`);
            const dow = dateObj.day();
            const isToday = dateObj.isSame(today, "day");
            const dateStr = dateObj.format("YYYY-MM-DD");
            const { regularLessons, provisionalLessons, practiceCount } = getScheduleCountByDate(dateStr);
            const hasSchedule = regularLessons > 0 || provisionalLessons > 0 || practiceCount > 0;

            return (
            <Box
                key={idx}
                onClick={() => handleDateClick(dateInfo)}
                sx={{
                height: "52px",
                p: 0.5,
                overflow: "hidden",
                cursor: hasSchedule && isCurrentMonth ? "pointer" : "default",
                borderRadius: 1,
                bgcolor: isToday
                  ? (darkMode ? "rgba(255, 152, 0, 0.15)" : "#fff3e0")
                  : "transparent",
                border: isToday
                  ? "2px solid"
                  : "1px solid",
                borderColor: isToday
                  ? "warning.main"
                  : (darkMode ? "grey.700" : "#e0e0e0"),
                opacity: !isCurrentMonth ? 0.3 : 1,
                transition: "all 0.2s",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                "&:hover": hasSchedule && isCurrentMonth ? {
                    bgcolor: darkMode ? "rgba(255,255,255,0.05)" : "#f5f5f5",
                    transform: "scale(1.02)"
                } : {}
                }}
            >
                <Typography
                variant="body2"
                sx={{
                    fontWeight: isToday ? "bold" : "normal",
                    color: dow === 0 ? "#d32f2f" : dow === 6 ? "#1976d2" : "inherit",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    lineHeight: 1.2,
                    mb: 0.25
                }}
                >
                {day}
                </Typography>
                
                <Box sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                alignContent: "center",
                gap: "2px",
                rowGap: "1px",
                flexWrap: "wrap",
                minHeight: 0,
                overflow: "hidden"
                }}>
                {isCurrentMonth && regularLessons > 0 && scheduleDot("#1976d2", regularLessons, "r")}
                {isCurrentMonth && provisionalLessons > 0 && scheduleDot("#ff9800", provisionalLessons, "p")}
                {isCurrentMonth && practiceCount > 0 && scheduleDot("#4caf50", practiceCount, "c")}
                </Box>
            </Box>
            );
        })}
          </Box>
        </MonthCalendarNav>

          {/* ✅ 범례 */}
          <Box sx={{ mt: 2.5, display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#1976d2" }} />
              <Typography variant="caption">레슨</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#ff9800" }} />
              <Typography variant="caption">{isAdminOrTeacher ? '임시예약' : '익월 레슨'}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#4caf50" }} />
              <Typography variant="caption">연습실</Typography>
            </Box>
          </Box>
      </Paper>

      {/* ✅ 요약 정보 */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, textAlign: "center" }}>
          이번 달 요약
        </Typography>
        <Box sx={{ display: "flex", alignItems: "stretch" }}>
          <Box sx={{ flex: 1, textAlign: "center", px: 1 }}>
            <Typography variant="body2" color="text.secondary">정식 레슨</Typography>
            <Typography variant="h5" fontWeight="bold" color="primary">
              {regularLessonsTotal}회
            </Typography>
          </Box>
          {provisionalLessonsTotal > 0 && (
            <Box sx={{ flex: 1, textAlign: "center", px: 1, borderLeft: darkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid #e0e0e0" }}>
              <Typography variant="body2" color="text.secondary">
                {isAdminOrTeacher ? '임시 레슨' : '익월 레슨'}
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: "#ff9800" }}>
                {provisionalLessonsTotal}회
              </Typography>
            </Box>
          )}
          <Box sx={{ flex: 1, textAlign: "center", px: 1, borderLeft: darkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid #e0e0e0" }}>
            <Typography variant="body2" color="text.secondary">총 연습실</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: "#4caf50" }}>
              {practices.length}회
            </Typography>
          </Box>
        </Box>
        
        {!isAdminOrTeacher && provisionalLessonsTotal > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            다음 달 레슨은 결제 후 정식 예약으로 전환됩니다.
          </Alert>
        )}
      </Paper>

      {/* ✅ 상세 다이얼로그 */}
      <Dialog
        open={detailDialog}
        onClose={() => setDetailDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight="bold">
            {selectedDate && dayjs(selectedDate).format("M월 D일")} 일정
          </Typography>
          <IconButton onClick={() => setDetailDialog(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
          {dailySchedule.map((schedule, idx) => (
            <Card key={idx} variant="outlined">
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* ✅ 레슨 타입 배지 */}
                    {schedule.type === 'lesson' && !schedule.is_provisional && (
                      <Chip
                        label="레슨"
                        size="small"
                        color="primary"
                        icon={<Piano />}
                      />
                    )}
                    {schedule.type === 'lesson' && schedule.is_provisional && (
                      <Chip
                        label={isAdminOrTeacher ? "임시예약" : "익월 레슨"}
                        size="small"
                        icon={<HourglassEmpty />}
                        sx={{
                          backgroundColor: '#ff9800',
                          color: '#fff',
                          fontWeight: 700,
                          "& .MuiChip-icon": {
                            color: '#fff'
                          }
                        }}
                      />
                    )}
                    {schedule.type === 'practice' && (
                      <Chip
                        label="연습실"
                        size="small"
                        color="success"
                        icon={<LibraryMusic />}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccessTime sx={{ fontSize: "1rem", color: "text.secondary" }} />
                    <Typography variant="body2" fontWeight="bold">
                      {schedule.start_time.slice(0, 5)} - {schedule.end_time.slice(0, 5)}
                    </Typography>
                  </Box>
                </Box>
                
                {schedule.type === 'lesson' && schedule.teacher && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                    <Person sx={{ fontSize: "1rem", color: "text.secondary" }} />
                    <Typography variant="body2">
                      {schedule.teacher.name} 선생님
                    </Typography>
                  </Box>
                )}
                
                {schedule.type === 'practice' && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {schedule.room_name}
                  </Typography>
                )}

                {/* ✅ 임시 예약 안내 */}
                {schedule.type === 'lesson' && schedule.is_provisional && (
                  <Alert severity={isAdminOrTeacher ? "warning" : "info"} sx={{ mt: 1.5 }}>
                    {isAdminOrTeacher 
                      ? "임시 예약 상태입니다. 다음 달 결제 후 정식 예약으로 전환됩니다."
                      : "회원님이 결제하기 전 다른 사용자가 예약 할 수 없도록 생성된 임시 레슨입니다. 결제 후 일반 레슨 상태로 전환됩니다."}
                  </Alert>
                )}
              </CardContent>
            </Card>
          ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MySchedulePage;